import * as React from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { CalendarSingle, CalendarRange } from './Calendar';
import { Input } from './Input';

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateTime(date: Date, time: string | null): string {
  const dateStr = formatDate(date);
  const t = time && /^\d{2}:\d{2}$/.test(time) ? time : '00:00';
  return `${dateStr}T${t}`;
}

export function parseCalendarDate(value: string): Date | null {
  const datePart = String(value).split('T')[0] || value;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) {
    const dt = new Date(value);
    return isNaN(dt.getTime()) ? null : dt;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return null;
  }
  return dt;
}

export function DatePicker({
  value,
  onChange,
  withTime = false,
  placeholder,
  className = '',
  id,
  disabled = false,
  'data-testid': dataTestId,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  onBlur,
}: {
  value?: string;
  onChange: (next: string) => void;
  withTime?: boolean;
  placeholder?: string;
  className?: string;
  /** Lands on the trigger button so a `<Label htmlFor>` can focus the picker. */
  id?: string;
  disabled?: boolean;
  /** Lands on the popover-trigger button so e2e specs can locate the
   *  picker without snooping at i18n labels. The calendar day cells
   *  inside the popover already expose accessible names via
   *  react-day-picker — specs target those by `getByRole('gridcell', ...)`. */
  'data-testid'?: string;
  /** Set by the form engine when the field carries an issue (#1797). */
  'aria-invalid'?: true;
  /** Points at the field's issue text (#1797). */
  'aria-describedby'?: string;
  /** Blur-time validation hook (#1797). */
  onBlur?: () => void;
}) {
  const { t } = useResourceKit();
  const resolvedPlaceholder = placeholder ?? t('rk.datepicker.selectDate');
  const [open, setOpen] = React.useState(false);
  const parsed = React.useMemo(() => {
    if (!value) return null;
    const [datePart, timePart] = String(value).split('T');
    const dt = parseCalendarDate(datePart || value);
    if (!dt) return null;
    return { date: dt, time: (timePart || '').slice(0, 5) || null };
  }, [value]);

  const [tempDate, setTempDate] = React.useState<Date | null>(parsed?.date || null);
  const [tempTime, setTempTime] = React.useState<string>(parsed?.time || '00:00');

  React.useEffect(() => {
    setTempDate(parsed?.date || null);
    setTempTime(parsed?.time || '00:00');
  }, [open]);

  const display = (() => {
    if (!parsed) return resolvedPlaceholder;
    return withTime
      ? `${formatDate(parsed.date)} ${parsed.time || '00:00'}`
      : formatDate(parsed.date);
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            id={id}
            disabled={disabled}
            className={`inline-flex items-center gap-2 border rounded-md px-3 py-2 bg-surface-1 hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface-1 ${className}`}
            data-testid={dataTestId}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            onBlur={onBlur}
          >
            <CalendarIcon size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground min-w-[8rem] text-left">{display}</span>
          </button>
        }
      />
      <PopoverContent className="w-auto p-2">
        <div className="flex flex-col gap-2">
          <CalendarSingle
            selected={tempDate || undefined}
            onSelect={(d) => {
              setTempDate(d || null);
              if (d && !withTime) {
                onChange(formatDate(d));
                setOpen(false);
              }
            }}
          />
          {withTime && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('rk.datepicker.time')}</span>
              <Input
                placeholder="HH:mm"
                value={tempTime}
                onChange={(e) => setTempTime(e.target.value)}
                className="w-28"
              />
              <button
                className="ml-auto text-sm px-2 py-1 border rounded"
                onClick={() => {
                  if (tempDate) {
                    onChange(formatDateTime(tempDate, tempTime));
                    setOpen(false);
                  }
                }}
                disabled={!tempDate}
              >
                {t('rk.datepicker.confirm')}
              </button>
              <button
                className="text-sm px-2 py-1"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                {t('rk.datepicker.clear')}
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DateRangePicker({
  value,
  onChange,
  placeholder,
  className = '',
}: {
  value?: [string, string] | string[];
  onChange: (next: [string, string]) => void;
  placeholder?: string;
  className?: string;
}) {
  const { t } = useResourceKit();
  const resolvedPlaceholder = placeholder ?? t('rk.datepicker.selectRange');
  const [open, setOpen] = React.useState(false);
  const parsed = React.useMemo(() => {
    const [a, b] = Array.isArray(value) ? value : [];
    const da = a ? parseCalendarDate(a) : null;
    const db = b ? parseCalendarDate(b) : null;
    return { from: da ?? undefined, to: db ?? undefined };
  }, [value]);

  const display = (() => {
    if (parsed.from && parsed.to) return `${formatDate(parsed.from)} — ${formatDate(parsed.to)}`;
    if (parsed.from) return `${formatDate(parsed.from)} — `;
    return resolvedPlaceholder;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            className={`inline-flex items-center gap-2 border rounded-md px-3 py-2 bg-surface-1 hover:bg-surface-2 ${className}`}
          >
            <CalendarIcon size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground min-w-[14rem] text-left">{display}</span>
          </button>
        }
      />
      <PopoverContent className="w-auto p-2">
        <CalendarRange
          selected={{ from: parsed.from, to: parsed.to }}
          onSelect={(range) => {
            if (range?.from && range?.to) {
              onChange([formatDate(range.from), formatDate(range.to)]);
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
